import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showToast, ACCREDITEDKYCWEBSERVICE, WEBSERVICE } from "../../CoreDataService";

const AccreditationVerificationPopup = ({ open, onClose, theme = "light" }) => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  // Dark mode colors
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    text: "#ffffff",
    textSecondary: "#b0b0b0",
    primary: "#90caf9",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    border: "#333333",
  };

  // Light mode colors
  const lightColors = {
    background: "#ffffff",
    paper: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    primary: "#1976d2",
    success: "#2e7d32",
    warning: "#ed6c02",
    error: "#d32f2f",
    info: "#0288d1",
    border: "#dee2e6",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [accreditationStatus, setAccreditationStatus] = useState(null);
  const [userDocsStatus, setUserDocsStatus] = useState(null);
  const [userTierDocsStatus, setUserTierDocsStatus] = useState(null);
  const [verificationStep, setVerificationStep] = useState(1); // 1: KYC, 2: Accreditation
  const [kycMessage, setKycMessage] = useState("");
  const [tier3Message, setTier3Message] = useState("");
  const [buyingCryptoAsset, setBuyingCryptoAsset] = useState("");

  useEffect(() => {
    if (open) {
      // Get the buying crypto asset from localStorage
      const asset = localStorage.getItem("buying_crypto_asset");
      setBuyingCryptoAsset(asset ? asset.toUpperCase() : "this asset");
      initializeVerification();
    }
  }, [open]);

  const initializeVerification = async () => {
    setLoading(true);
    try {
      await getUserDetails();
    } catch (error) {
      console.error("Initialization error:", error);
      showToast("error", "Failed to load verification status");
    } finally {
      setLoading(false);
    }
  };

  const getUserDetails = async () => {
    const token = localStorage.getItem("access_token");
    const uuid = localStorage.getItem("uuid");

    if (!token || !uuid) {
      showToast("error", "Authentication required");
      return;
    }

    try {
      const response = await axios.post(
        `${WEBSERVICE}/user/GetUserDetails`,
        { uuid },
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.error?.error_data === 0) {
        const userData = response.data.userResult;
        setUserDetails(userData);

        // Check user documents status
        const userDocsStatus = userData.userDocsStatus || "";
        const userTierDocsStatus = userData.userTierDocsStatus || "";

        setUserDocsStatus(userDocsStatus);
        setUserTierDocsStatus(userTierDocsStatus);

        // Determine KYC status messages
        let kycMsg = "";
        if (userDocsStatus === "") {
          kycMsg = "You are in Tier 1, please submit documents for Tier 2.";
        } else if (userDocsStatus === "1") {
          kycMsg = "Tier 2 Identity verification documents verified.";
        } else if (userDocsStatus === "0" || userDocsStatus === "2") {
          kycMsg = "Tier 2 Identity verification documents submitted.";
        }
        setKycMessage(kycMsg);

        // Determine Tier 3 status message
        let tier3Msg = "";
        if (userTierDocsStatus === "") {
          tier3Msg = "You are already verified for Tier 2.";
        } else if (userTierDocsStatus === "0") {
          tier3Msg = "Tier 3 Identity verification documents submitted.";
        } else if (userTierDocsStatus === "1") {
          tier3Msg = "Tier 3 Identity verification documents verified.";
        } else if (userTierDocsStatus === "2") {
          tier3Msg = "Tier 3 Identity verification documents declined, please submit again.";
        }
        setTier3Message(tier3Msg);

        // Check accreditation status if KYC is verified
        const userId = userData?.user_id || userData?.userId || userData?.id;
        if (userDocsStatus === "1" && userId) {
          await checkAccreditationStatus(userId);
          setVerificationStep(2);
        } else {
          setVerificationStep(1);
        }
      } else {
        showToast("error", response?.data?.error?.error_msg || "Failed to fetch user details");
      }
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.error?.error_msg || "Failed to fetch user details"
      );
    }
  };

  const checkAccreditationStatus = async (userId) => {
    const token = localStorage.getItem("access_token");

    try {
      const response = await axios.get(
        `${ACCREDITEDKYCWEBSERVICE}/accreditation/status/${userId}`,
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        setAccreditationStatus(response.data.data);
      } else {
        setAccreditationStatus(null);
      }
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        // No existing application
        setAccreditationStatus(null);
      } else {
        console.error("Error checking accreditation:", error);
      }
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      APPROVED: colors.success,
      PENDING: colors.warning,
      REJECTED: colors.error,
      DRAFT: colors.info,
      EXPIRED: colors.error,
      CANCELLED: colors.error,
      UNDER_REVIEW: colors.info,
      ADDITIONAL_INFO_REQUIRED: colors.warning,
    };
    return statusColors[status] || colors.textSecondary;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      APPROVED: "Approved",
      PENDING: "Pending Review",
      REJECTED: "Rejected",
      DRAFT: "Draft Application",
      EXPIRED: "Expired",
      CANCELLED: "Cancelled",
      UNDER_REVIEW: "Under Review",
      ADDITIONAL_INFO_REQUIRED: "More Info Needed",
    };
    return statusLabels[status] || status;
  };

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleContinue = () => {
    if (verificationStep === 1) {
      handleNavigate("/identity-verification");
    } else {
      handleNavigate("/accredited-investor-verification");
    }
  };

  const renderKYCStep = () => (
    <Box sx={{ textAlign: "center" }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: `${colors.warning}20`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
        }}
      >
        <WarningIcon sx={{ fontSize: 40, color: colors.warning }} />
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: colors.text,
          mb: 2,
          fontSize: isMobile ? "1.25rem" : "1.5rem",
        }}
      >
        KYC Verification Required
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: colors.textSecondary,
          mb: 5, // ✅ CHANGED: Increased from 3 to 5 to add more spacing before the status card
          lineHeight: 1.6,
          fontSize: isMobile ? "0.9rem" : "1rem",
        }}
      >
        Before proceeding with accredited investor verification, you need to complete your identity verification.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 0, // ✅ CHANGED: Reduced from 3 to 0 to minimize gap with Maybe Later section
          mt: 4, // ✅ ADDED: Additional top margin for better spacing above the card
          backgroundColor: theme === "dark" ? "#2d2d2d" : "#f8f9fa",
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: colors.text,
              mr: 1.5,
            }}
          >
            Current KYC Status
          </Typography>
          <Chip
            label={getKYCStatusLabel()}
            size="small"
            sx={{
              backgroundColor: getKYCStatusColor(),
              color: "white",
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: colors.textSecondary,
            display: "block",
          }}
        >
          {kycMessage}
        </Typography>
      </Paper>
    </Box>
  );

  const renderAccreditationStep = () => {
    const status = accreditationStatus?.status;
    const isApproved = status === "APPROVED";
    const showAccreditationRequired = !isApproved;

    return (
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: showAccreditationRequired
              ? `${colors.warning}20`
              : `${colors.success}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          {showAccreditationRequired ? (
            <WarningIcon sx={{ fontSize: 40, color: colors.warning }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 40, color: colors.success }} />
          )}
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text,
            mb: 2,
            fontSize: isMobile ? "1.25rem" : "1.5rem",
          }}
        >
          {isApproved
            ? "Accredited Investor Verified"
            : "Accredited Investor Verification Required"}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: colors.textSecondary,
            mb: 3,
            lineHeight: 1.6,
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
        >
          {isApproved
            ? "Congratulations! Your accredited investor status has been verified."
            : `Trading for ${buyingCryptoAsset} is currently limited to accredited investors. Please complete the accredited investor KYC verification process to continue.`}
        </Typography>

        {showAccreditationRequired && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 0, // Changed from 2 to 0 to reduce gap with Maybe Later section
              mt: 4, // Increased from 2 to 4 to add more spacing above the status card
              backgroundColor: theme === "dark" ? "#2d2d2d" : "#f8f9fa",
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: colors.text,
                  mr: 1.5,
                }}
              >
                Current Accreditation Status
              </Typography>
              {status ? (
                <Chip
                  label={getStatusLabel(status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(status),
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Chip
                  label="Not Started"
                  size="small"
                  sx={{
                    backgroundColor: colors.textSecondary,
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            {status && (
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  display: "block",
                }}
              >
                Application ID: #{accreditationStatus.applicationId}
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  const getKYCStatusLabel = () => {
    if (userDocsStatus === "0") return "Submitted";
    if (userDocsStatus === "1") return "Verified";
    if (userDocsStatus === "2") return "Rejected";
    return "Not Started";
  };

  const getKYCStatusColor = () => {
    if (userDocsStatus === "1") return colors.success;
    if (userDocsStatus === "0" || userDocsStatus === "2") return colors.warning;
    return colors.error;
  };

  const getButtonText = () => {
    if (verificationStep === 1) {
      return "Complete KYC Verification";
    } else {
      const status = accreditationStatus?.status;
      if (status === "APPROVED") return "View Status";
      if (status === "DRAFT") return "Continue Application";
      if (status === "REJECTED" || status === "EXPIRED") return "Re-apply";
      return "Start Verification";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: theme === "dark"
            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
            : "0 8px 32px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pr: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text,
            fontSize: isMobile ? "1.35rem" : "1.65rem",
          }}
        >
          Verification Status
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            position: "absolute",
            right: 16,
            "&:hover": {
              backgroundColor: theme === "dark" ? "#333" : "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 4 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress sx={{ color: colors.primary, mb: 2 }} />
            <Typography variant="body1" sx={{ color: colors.textSecondary }}>
              Checking verification status...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Progress Indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                position: "relative",
              }}
            >
              {/* Step 1: KYC */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: verificationStep >= 1 ? colors.primary : colors.border,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  zIndex: 2,
                }}
              >
                1
              </Box>

              {/* Connecting Line */}
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  backgroundColor: verificationStep >= 2 ? colors.primary : colors.border,
                  mx: 2,
                  maxWidth: 100,
                }}
              />

              {/* Step 2: Accreditation */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: verificationStep >= 2 ? colors.primary : colors.border,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  zIndex: 2,
                }}
              >
                2
              </Box>
            </Box>

            {/* Step Labels */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 3,
                px: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: verificationStep === 1 ? 600 : 400,
                  color: verificationStep === 1 ? colors.primary : colors.textSecondary,
                }}
              >
                KYC Verification
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: verificationStep === 2 ? 600 : 400,
                  color: verificationStep === 2 ? colors.primary : colors.textSecondary,
                }}
              >
                Investor Verification
              </Typography>
            </Box>

            {/* Content */}
            {verificationStep === 1 ? renderKYCStep() : renderAccreditationStep()}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: `1px solid ${colors.border}`,
          justifyContent: "space-between",
          flexDirection: isMobile ? "column-reverse" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            borderColor: colors.border,
            color: colors.textSecondary,
            "&:hover": {
              borderColor: colors.primary,
              backgroundColor: `${colors.primary}10`,
            },
            width: isMobile ? "100%" : "auto",
          }}
        >
          Maybe Later
        </Button>

        {!loading && (
          <Button
            onClick={handleContinue}
            variant="contained"
            endIcon={<ArrowForwardIcon sx={{ ml: 1.5 }} />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: colors.primary,
              "&:hover": {
                backgroundColor: theme === "dark" ? "#1565c0" : "#115293",
              },
              width: isMobile ? "100%" : "auto",
              gap: 1.5,
            }}
          >
            {getButtonText()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AccreditationVerificationPopup;